import { z, ZodError } from 'zod';

/**
 * Flattens nested Zod errors into a single-level object with keys separated by '-'
 */
export function flattenZodErrors(error: ZodError<unknown>): Record<string, string> {
  const flatErrors: Record<string, string> = {};
  error.issues.forEach((issue) => {
    const key = issue.path.join('-');
    if (!flatErrors[key]) {
      flatErrors[key] = issue.message;
    }
  });
  return flatErrors;
}

type ZObjectType = z.ZodType<Record<string | number, unknown>>;

type ZodParams<T extends ZObjectType> = {
  onSuccess(data: T['_output']): void;
  onError(error: Partial<Record<keyof T['_output'], string>>): void;
  data: Record<string, unknown>;
  schema: T;
};

export type ValidationError<T extends ZObjectType> = Partial<Record<keyof T['_output'], string>>;

/**
 * Turns a ValidationError map into a single human-readable string (field: message, ...),
 * for surfacing validation failures on fields that have no dedicated inline error UI.
 */
export function formatValidationErrors<T extends ZObjectType>(
  errors: ValidationError<T> | undefined
): string {
  const entries = Object.entries(errors ?? {});
  if (entries.length === 0) {
    return 'Okänt fel vid validering.';
  }
  return entries.map(([field, message]) => `${field}: ${message}`).join(', ');
}

export const handleZodValidation = <T extends ZObjectType>(params: ZodParams<T>) => {
  const { data, onError, onSuccess, schema } = params;

  try {
    const res = schema.parse(data);
    onSuccess(res);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const formattedErr = flattenZodErrors(error);
      onError(formattedErr as Record<keyof T['_output'], string>);
    } else {
      throw new Error(String(error));
    }
  }
};
